import Chart from "./Chart";
import { RecordData } from "@/common/types/RecordData";
import { DJ_LEVEL, DJ_LEVEL_STRING, DjLevel } from "@/common/types/DjLevel";
import { CLEAR_TYPE_STRING, ClearType } from "@/common/types/ClearType";
import "@/types/Date.extends";
import { Radar, valueFormat } from "@/common/types/Radar";

export default class Record {
  readonly chart: Chart;
  readonly score: number;
  readonly pGreat: number;
  readonly great: number;
  readonly missCount: number;
  readonly clearType: ClearType;
  readonly date: Date | null;
  readonly playVersion: number | null;
  readonly playCount: number;

  constructor(chart: Chart, data: RecordData) {
    this.chart = chart;
    this.score = data.score;
    this.pGreat = data.pGreat;
    this.great = data.great;
    this.missCount = data.missCount;
    this.clearType = data.clearType;
    this.date = data.date;
    this.playVersion = data.playVersion;
    this.playCount = data.playCount;
  }

  get scoreRate(): number | null {
    const max = this.chart.djLevelScore(DJ_LEVEL.MAX);
    if (max === null) return null;

    return this.score / max;
  }

  get scoreRateString(): string {
    return this.scoreRate === null
      ? "-"
      : new Intl.NumberFormat("ja", {
          style: "percent",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(this.scoreRate);
  }

  get exDjLevel(): DjLevel | null {
    if (this.score <= 0) {
      return DJ_LEVEL.NO_SCORE;
    }
    for (const djLevel of Object.values(DJ_LEVEL)) {
      const djLevelScore = this.chart.djLevelScore(djLevel);
      if (djLevelScore === null) return null;

      if (djLevelScore <= this.score) {
        return djLevel;
      }
    }
    return DJ_LEVEL.F;
  }

  get exDjLevelString(): string {
    return this.exDjLevel === null ? "-" : DJ_LEVEL_STRING[this.exDjLevel];
  }

  get djLevel(): DjLevel | null {
    const exDjLevel = this.exDjLevel;
    return exDjLevel === null ? null : (Math.min(Math.max(exDjLevel, DJ_LEVEL.F), DJ_LEVEL.AAA) as DjLevel);
  }

  get djLevelString(): string {
    return this.djLevel === null ? "-" : DJ_LEVEL_STRING[this.djLevel];
  }

  get djLevelDiff(): number | null {
    const currentDjLevel = this.djLevel;
    if (currentDjLevel === null) return null;

    const currentScore = this.chart.djLevelScore(currentDjLevel);
    if (currentScore === null) return null;

    return this.score - currentScore;
  }

  get djLevelDiffString(): string {
    return this.djLevelDiff === null ? "-" : this.djLevelString + "+" + this.djLevelDiff.toString();
  }

  get nextDjLevel(): DjLevel | null {
    return this.djLevel === null ? null : ((this.djLevel + 1) as DjLevel);
  }

  get nextDjLevelString(): string {
    return this.nextDjLevel === null ? "-" : DJ_LEVEL_STRING[this.nextDjLevel];
  }

  get nextDjLevelDiff(): number | null {
    const nextDjLevel = this.nextDjLevel;
    if (nextDjLevel === null) return null;

    const nextScore = this.chart.djLevelScore(nextDjLevel);
    if (nextScore === null) return null;

    return nextScore - this.score;
  }

  get nextDjLevelDiffString(): string {
    return this.nextDjLevelDiff === null ? "-" : this.nextDjLevelString + "-" + this.nextDjLevelDiff.toString();
  }

  get nearerDjLevelDiffString(): string {
    const djLevelDiff = this.djLevelDiff;
    const nextDjLevelDiff = this.nextDjLevelDiff;
    if (djLevelDiff === null || nextDjLevelDiff === null) return "-";

    return nextDjLevelDiff < djLevelDiff ? this.nextDjLevelDiffString : this.djLevelDiffString;
  }

  get missCountString(): string {
    return this.missCount === 65535 ? "-" : this.missCount.toString();
  }

  get clearTypeString(): string {
    return CLEAR_TYPE_STRING[this.clearType];
  }

  get isPrefect(): boolean {
    return this.pGreat + this.great === this.chart.notes;
  }

  get dateString(): string {
    return this.date !== null ? this.date.string() : "-";
  }

  get djPoint(): number {
    const clear = [0, 0, 0, 10, 15, 20, 25, 30][this.clearType];
    const djLevel = this.djLevel !== null ? [0, 0, 0, 0, 0, 0, 10, 15, 20, 20][this.djLevel] : 0;
    return (this.score * (100 + clear + djLevel)) / 10000.0;
  }

  get djPointString(): string {
    return this.djPoint.toFixed(2);
  }

  get radar(): Radar | null {
    return this.chart.radar === null || this.scoreRate === null
      ? null
      : {
          notes: this.chart.radar.notes * this.scoreRate,
          chord: this.chart.radar.chord * this.scoreRate,
          peak: this.chart.radar.peak * this.scoreRate,
          charge: this.chart.radar.charge * this.scoreRate,
          scratch: this.chart.radar.scratch * this.scoreRate,
          soflan: this.chart.radar.soflan * this.scoreRate,
        };
  }

  get radarString(): { [P in keyof Radar]: string } | null {
    return {
      notes: this.radar ? valueFormat(this.radar.notes) : "-",
      chord: this.radar ? valueFormat(this.radar.chord) : "-",
      peak: this.radar ? valueFormat(this.radar.peak) : "-",
      charge: this.radar ? valueFormat(this.radar.charge) : "-",
      scratch: this.radar ? valueFormat(this.radar.scratch) : "-",
      soflan: this.radar ? valueFormat(this.radar.soflan) : "-",
    };
  }
}

export const COMPARE_KEYS = [
  { value: "score", title: "スコア" },
  { value: "clearType", title: "クリアタイプ" },
  { value: "missCount", title: "ミスカウント" },
];
